import type {
  BulkItemAction, Category, Deal, EnrichResult, Feedback, Item, ItemCreate, ItemUpdate, Mood, Need, Place,
  RestFilters, Selection, SelectionCreate, SelectionUpdate, Settings, ShuffleResponse, Source, SourceDefaults, SourcePreview, SourceType, SyncStats,
} from "@instead/shared";
import { outbox } from "./outbox.ts";

const BASE = "/api";
const TOKEN_KEY = "instead.token";
const INVITE_KEY = "instead.invite";

export const auth = {
  token: (): string | null => localStorage.getItem(TOKEN_KEY),
  setToken: (t: string) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

/** Beta gate (§5.11): held locally until the first authorized call consumes it. */
export const invite = {
  get: (): string | null => localStorage.getItem(INVITE_KEY),
  set: (code: string) => localStorage.setItem(INVITE_KEY, code),
  clear: () => localStorage.removeItem(INVITE_KEY),
};

export class ApiRequestError extends Error {
  /** `requestId` is the server's correlation id from the error envelope — shown as a short "ref" so a report can be matched to a log line. */
  constructor(public status: number, public code: string, message: string, public requestId?: string) {
    super(message);
  }
}

function headers(extra: Record<string, string> = {}): Record<string, string> {
  return {
    ...(auth.token() ? { authorization: `Bearer ${auth.token()}` } : {}),
    ...(invite.get() ? { "x-invite-code": invite.get()! } : {}),
    ...extra,
  };
}

async function handle<T>(res: Response): Promise<T> {
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    const code = json?.error?.code ?? "INTERNAL";
    if (res.status === 401) {
      auth.clear();
      // the account is gone (D-33): nothing of it may linger on this device
      if (code === "ACCOUNT_DELETED") void import("./device.ts").then((m) => m.wipeDevice());
    }
    if (code === "BETA_INVITE_REQUIRED" && !window.location.pathname.startsWith("/signin")) {
      window.location.assign("/signin?beta=required");
    }
    throw new ApiRequestError(res.status, code, json?.error?.message ?? "Something went wrong.", typeof json?.error?.requestId === "string" ? json.error.requestId : undefined);
  }
  if (invite.get()) invite.clear(); // consumed (or unneeded) once any call succeeds
  return json as T;
}

async function call<T>(method: string, path: string, body?: unknown, opts: { keepalive?: boolean } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: headers(body !== undefined ? { "content-type": "application/json" } : {}),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      // a small write the person may leave the page right after (a settings
      // change, then close the app) still reaches the server
      ...(opts.keepalive ? { keepalive: true } : {}),
    });
  } catch (networkError) {
    // Offline (D-8): item edits queue locally and replay when back online.
    // Invisible to the person — the edit is simply kept (round 2).
    if (method === "PATCH" && /^\/v1\/items\/[^/]+$/.test(path)) {
      outbox.enqueue({ path, method, body });
      throw new ApiRequestError(0, "OFFLINE_QUEUED", "Saved");
    }
    throw networkError;
  }
  return handle<T>(res);
}

export interface Me { id: string; email: string; displayName: string; settings: Settings }
/** A book card from /v1/books/isbn (Open Library, Google Books filling the gaps). */
export interface Book { isbn: string; title: string; subtitle: string | null; description: string | null; authors: string[]; pages: number | null; thumbUrl: string | null; link: string }
export interface ItemPage { items: Item[]; nextCursor: string | null }

export const api = {
  devLogin: (email: string, name = "", inviteCode?: string) =>
    call<{ token: string }>("POST", "/dev/login", { email, name, ...(inviteCode ? { inviteCode } : {}) }),
  me: () => call<Me>("GET", "/v1/me"),
  items: {
    list: (params: Record<string, string> = {}) =>
      call<ItemPage>("GET", `/v1/items?${new URLSearchParams(params)}`),
    byIds: async (ids: string[]): Promise<Item[]> => {
      if (ids.length === 0) return [];
      const page = await call<ItemPage>("GET", `/v1/items?${new URLSearchParams({ ids: ids.join(","), limit: "200" })}`);
      const byId = new Map(page.items.map((i) => [i.id, i]));
      return ids.map((id) => byId.get(id)).filter((i): i is Item => !!i);
    },
    get: (id: string) => call<Item>("GET", `/v1/items/${id}`),
    create: (body: ItemCreate) => call<Item>("POST", "/v1/items", body),
    update: (id: string, body: ItemUpdate) => call<Item>("PATCH", `/v1/items/${id}`, body),
    remove: (id: string) => call<{ ok: true }>("DELETE", `/v1/items/${id}`),
    bulk: (body: BulkItemAction) => call<{ affected: number }>("POST", "/v1/items/bulk", body),
    open: (id: string) => call<{ ok: true }>("POST", `/v1/items/${id}/open`),
    /** Upload the (client-cropped) cover; the server re-processes it. */
    uploadCover: async (id: string, blob: Blob): Promise<Item> => {
      const res = await fetch(`${BASE}/v1/items/${id}/cover`, { method: "PUT", headers: headers({ "content-type": blob.type || "application/octet-stream" }), body: blob });
      return handle<Item>(res);
    },
    removeCover: (id: string) => call<Item>("DELETE", `/v1/items/${id}/cover`),
  },
  categories: {
    list: () => call<{ categories: Category[] }>("GET", "/v1/categories"),
    create: (body: { name: string; colorToken?: Category["colorToken"]; parentId?: string }) =>
      call<Category>("POST", "/v1/categories", body),
    update: (id: string, body: { name?: string; colorToken?: Category["colorToken"]; sortOrder?: number }) =>
      call<Category>("PATCH", `/v1/categories/${id}`, body),
    remove: (id: string) => call<{ ok: true }>("DELETE", `/v1/categories/${id}`),
    reset: () => call<{ ok: true }>("POST", "/v1/categories/reset"),
  },
  moods: {
    list: () => call<{ moods: Mood[] }>("GET", "/v1/moods"),
    create: (name: string) => call<Mood>("POST", "/v1/moods", { name }),
    update: (id: string, body: { name?: string; sortOrder?: number }) => call<Mood>("PATCH", `/v1/moods/${id}`, body),
    remove: (id: string) => call<{ ok: true }>("DELETE", `/v1/moods/${id}`),
    reset: () => call<{ ok: true }>("POST", "/v1/moods/reset"),
  },
  needs: {
    list: () => call<{ needs: Need[] }>("GET", "/v1/needs"),
    create: (name: string) => call<Need>("POST", "/v1/needs", { name }),
    update: (id: string, name: string) => call<Need>("PATCH", `/v1/needs/${id}`, { name }),
    remove: (id: string) => call<{ ok: true }>("DELETE", `/v1/needs/${id}`),
  },
  places: {
    list: () => call<{ places: Place[] }>("GET", "/v1/places"),
    get: (id: string) => call<Place>("GET", `/v1/places/${id}`),
    create: (body: Omit<Place, "id" | "itemCount">) => call<Place>("POST", "/v1/places", body),
    update: (id: string, body: Partial<Omit<Place, "id" | "itemCount">>) => call<Place>("PATCH", `/v1/places/${id}`, body),
    remove: (id: string) => call<{ ok: true }>("DELETE", `/v1/places/${id}`),
  },
  settings: {
    get: () => call<Settings>("GET", "/v1/settings"),
    put: (s: Settings) => call<Settings>("PUT", "/v1/settings", s, { keepalive: true }),
  },
  picks: {
    shuffle: (body: { count: number; filters: RestFilters; includeDone?: boolean; excludeItemIds?: string[]; persist?: boolean }) =>
      call<ShuffleResponse>("POST", "/v1/picks/shuffle", body),
    choose: (pickId: string, itemId: string) => call<{ ok: true }>("POST", `/v1/picks/${pickId}/choose`, { itemId }),
  },
  deal: {
    get: () => call<{ deal: Deal | null }>("GET", "/v1/deal"),
    put: (itemIds: string[], relaxed?: boolean) => call<{ deal: Deal }>("PUT", "/v1/deal", { itemIds, ...(relaxed !== undefined ? { relaxed } : {}) }),
    clear: () => call<{ ok: true }>("DELETE", "/v1/deal"),
  },
  selections: {
    list: () => call<{ selections: Selection[] }>("GET", "/v1/selections"),
    get: (id: string) => call<Selection>("GET", `/v1/selections/${id}`),
    create: (body: SelectionCreate) => call<Selection>("POST", "/v1/selections", body),
    update: (id: string, body: SelectionUpdate) => call<Selection>("PATCH", `/v1/selections/${id}`, body),
    remove: (id: string) => call<{ ok: true }>("DELETE", `/v1/selections/${id}`),
  },
  feedback: (body: Feedback) => call<{ ok: true }>("POST", "/v1/feedback", body),
  sources: {
    list: () => call<{ sources: Source[] }>("GET", "/v1/sources"),
    preview: (url: string, type?: SourceType) => call<SourcePreview>("POST", "/v1/sources/preview", { url, ...(type ? { type } : {}) }),
    create: (url: string, opts: { type?: SourceType; exclude?: string[]; defaults?: SourceDefaults | null } = {}) =>
      call<{ source: Source; stats: SyncStats }>("POST", "/v1/sources", { url, ...(opts.type ? { type: opts.type } : {}), exclude: opts.exclude ?? [], ...(opts.defaults ? { defaults: opts.defaults } : {}) }),
    sync: (id: string) => call<{ source: Source; stats: SyncStats }>("POST", `/v1/sources/${id}/sync`),
    /** `defaults` reaches only the items the source adds from now on (D-36); null clears */
    update: (id: string, body: { label?: string; enabled?: boolean; defaults?: SourceDefaults | null }) =>
      call<Source>("PATCH", `/v1/sources/${id}`, body),
    remove: (id: string, items: "keep" | "remove") =>
      call<{ ok: true; removedItems: number }>("DELETE", `/v1/sources/${id}?items=${items}`),
  },
  enrich: (url: string) => call<EnrichResult>("POST", "/v1/enrich", { url }),
  books: {
    isbn: (isbn: string) => call<Book>("GET", `/v1/books/isbn/${encodeURIComponent(isbn)}`),
  },
  ai: {
    status: () => call<{ connected: boolean; since: string | null }>("GET", "/v1/ai/status"),
    disconnect: () => call<{ ok: true }>("POST", "/v1/ai/disconnect"),
  },
  account: {
    consents: () =>
      call<{ consents: { kind: string; version: string; grantedAt: string; revokedAt: string | null }[] }>("GET", "/v1/consents"),
    /** Records acceptance of the terms + privacy notice at `version` (D-38); idempotent. */
    acceptTerms: (version: string) => call<{ ok: true; kind: "terms"; version: string }>("POST", "/v1/consents", { kind: "terms", version }),
    remove: () => call<{ ok: true; deleted: true }>("POST", "/v1/account/delete", { confirm: "DELETE" }),
    /** Downloads the export zip via the browser. */
    export: async (): Promise<void> => {
      const res = await fetch(`${BASE}/v1/export`, { headers: headers() });
      if (!res.ok) throw new ApiRequestError(res.status, "INTERNAL", "Export failed — try again.");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `instead-export-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    },
  },
};

/** The MCP connector URL for this deployment — what users paste into their AI
 *  app (D-23). Same-origin: the web host proxies /api to the API. */
export const mcpUrl = (): string => `${window.location.origin}${BASE}/mcp`;
